import React, { useEffect, useState } from "react";
import {
  MapPin,
  Scale,
  CheckCircle,
  Edit3,
  FastForward,
  AlertTriangle,
  Tag,
  Activity as ActivityIcon,
  Loader2,
  ListOrdered,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    total: 0,
    validated: 0,
    invalid: 0,
    skipped: 0,
    manual: 0,
    auto: 0,
    valid_percentage: "0.00", // MUST be here
    rfid_percentage: "0.00",
    burn: 0,
    ear: 0,
    totalManual: 0,
    totalValidated: 0,
    totals: 0,
    totalSkipped: 0,
    totalAuto: 0,
  });
  const [userRole, setUserRole] = useState("");
  useEffect(() => {
    const role = localStorage.getItem("role");
    setUserRole(role);
    fetchData();
    const interval = setInterval(fetchData, 2000);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("userToken");
      const role = localStorage.getItem("role");
      const id = localStorage.getItem("id");
      const recRes = await fetch(
        `https://db2sg.com/tagweigh/api/reports?action=dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const recData = await recRes.json();

      const weighings = recData || [];

      // Calculate stats from the weighings array
      setDashboardStats({
        sites: weighings.sites,
        total: weighings.todayWeighings,
        validated: weighings.todayValidated,
        invalid: weighings.todayInvalid,
        skipped: weighings.todaySkipped,
        manual: weighings.todayManual,
        auto: weighings.todayAuto,
        totals: weighings.total,
        totalValidated: weighings.totalValidated,
        totalSkipped: weighings.totalSkipped,
        totalAuto: weighings.totalAuto,
        totalManual: weighings.totalManual,
        valid_percentage: percent(
          weighings.todayValidated,
          weighings.todayWeighings
        ),
        rfid_percentage: percent(weighings.todayRfid, weighings.todayWeighings),
        burn: weighings.todayBurn,
        ear: weighings.todayEar,
      });
      const weighRes = await fetch(
        `https://db2sg.com/tagweigh/api/weighings?limit=7&offset=0`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const weighData = await weighRes.json();
      //let allRecords = weighData.weighings || [];
      let allRecords = (weighData.weighings || []).sort(
        (a, b) => Number(b.id) - Number(a.id) // newest first
      );
      console.log(role, id);
      if (role === "user") {
        allRecords = allRecords.filter(
          (rec) => rec.user?.toString() === id?.toString()
        );
      }

      setRecords(allRecords.slice(0, 6));
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  };
  const navigate = useNavigate();

  const percent = (value, total) => {
    if (!total || total === 0) return "0.00";
    return ((value * 100) / total).toFixed(2);
  };
  const formatJerusalemDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return "";

    // Combine UTC date + time
    const utcDate = new Date(`${dateStr}T${timeStr}Z`);

    return utcDate.toLocaleString("en-IL", {
      timeZone: "Asia/Jerusalem",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };
  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-500" size={48} />
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-8 duration-1000 ease-out max-w-full overflow-x-hidden">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-5">
        <MiniStatCard
          icon={<MapPin size={18} />}
          label="אתרים סה״כ"
          value={dashboardStats.sites}
          color="from-blue-500 to-blue-600"
          onClick={() => navigate("/monitor")}
        />
        <MiniStatCard
          icon={<ListOrdered size={18} />}
          label="סה״כ תג"
          value={dashboardStats.totals}
          color="from-emerald-500 to-emerald-600"
          onClick={() => navigate("/weighings")}
        />
        <MiniStatCard
          icon={<CheckCircle size={18} />}
          label="מאומתים תג"
          value={dashboardStats.totalValidated}
          color="from-cyan-500 to-cyan-600"
          onClick={() =>
            navigate("/weighings", { state: { filter: "validated" } })
          }
        />
        <MiniStatCard
          icon={<Edit3 size={18} />}
          label="הזנות ידניות תג"
          value={dashboardStats.totalManual}
          color="from-amber-500 to-amber-600"
          onClick={() =>
            navigate("/weighings", { state: { filter: "manual" } })
          }
        />
        <MiniStatCard
          icon={<FastForward size={18} />}
          label="דילוגים תג"
          value={dashboardStats.totalSkipped}
          color="from-slate-600 to-slate-800"
          onClick={() =>
            navigate("/weighings", { state: { filter: "skipped" } })
          }
        />
        <MiniStatCard
          icon={<Tag size={18} />}
          label="אוֹטוֹמָטִי תג"
          value={dashboardStats.totalAuto}
          color="from-red-500 to-red-600"
          onClick={() => navigate("/weighings", { state: { filter: "auto" } })}
        />
      </div>

      <div className="bg-white rounded-[2.5rem] mb-5 shadow-sm border border-slate-100 overflow-hidden">
        {userRole !== "user" && (
          <div className="xl:col-span-2 bg-white rounded-[2.5rem] shadow-sm border border-slate-100 relative overflow-hidden">
            <div className="p-8 border-b border-slate-50 flex justify-between items-center">
              <h3 className="text-xl font-black text-slate-800 flex items-center gap-3 whitespace-nowrap">
                <ActivityIcon
                  className="text-emerald-500 whitespace-nowrap"
                  size={22}
                />
                פילוח שיטות הזנה ואימות (היום)
              </h3>
            </div>

            {/* Grid optimized for 5 items to not break container */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 p-6 md:p-10">
              <BreakdownItem
                value={dashboardStats.auto}
                label="אוטומטי (RFID)"
                icon={<Tag size={20} />}
                color="text-emerald-500"
                bg="bg-emerald-50"
                onClick={() =>
                  navigate("/weighings", {
                    state: { filter: "auto", time: "today" },
                  })
                }
              />
              <BreakdownItem
                value={dashboardStats.manual}
                label="ידניות"
                icon={<Edit3 size={20} />}
                color="text-blue-500"
                bg="bg-blue-50"
                onClick={() =>
                  navigate("/weighings", {
                    state: { filter: "manual", time: "today" },
                  })
                }
              />
              <BreakdownItem
                value={dashboardStats.validated}
                label="מאומתים"
                icon={<CheckCircle size={20} />}
                color="text-amber-500"
                bg="bg-amber-50"
                onClick={() =>
                  navigate("/weighings", {
                    state: { filter: "validated", time: "today" },
                  })
                }
              />
              <BreakdownItem
                value={dashboardStats.total}
                label="שקילות היום"
                icon={<Scale size={20} />}
                color="text-red-500"
                bg="bg-red-50"
                onClick={() =>
                  navigate("/weighings", { state: { time: "today" } })
                }
              />
              <BreakdownItem
                value={dashboardStats.skipped}
                label="דילוג"
                icon={<FastForward size={20} />}
                color="text-slate-400"
                bg="bg-slate-50"
                onClick={() =>
                  navigate("/weighings", {
                    state: { filter: "skipped", time: "today" },
                  })
                }
              />
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-8 border-b border-slate-50 flex justify-between items-center">
          <h3 className="text-xl font-black text-slate-800">
            🕐 פעילות אחרונה
          </h3>
          <button
            onClick={() => navigate("/weighings")}
            className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-colors"
          >
            View All History
          </button>
        </div>
        <div className="overflow-x-auto w-full">
          <table className="w-full text-center min-w-[800px]">
            <thead>
              <tr className="bg-slate-50/50 text-grey-400 uppercase text-[10px] font-black tracking-widest">
                <th className="px-4 py-3">אתר</th>
                {/* <th className="px-4 py-3">סטטוס</th> */}
                <th className="px-4 py-3">תג</th>
                <th className="px-4 py-3 text-center whitespace-nowrap">
                  משקל (kg)
                </th>
                <th className="px-4 py-3 text-center">אימות</th>
                <th className="px-4 py-3">תאריך ושעה </th>
                {/* <th className="px-4 py-3">תאריך ירושלים</th> */}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {records.map((stat, i) => (
                <ActivityRow
                  key={stat.id}
                  site={stat.name}
                  tag={stat.tag_number}
                  method={stat.mode}
                  weight={stat.weight}
                  validated={stat.validated === 1 || stat.validated === "1"}
                  time={stat.weight_date + " " + stat.weight_time}
                  date={stat.weight_date}
                  time2={stat.weight_time}
                  formatJerusalemDateTime={formatJerusalemDateTime}
                  status={
                    stat.validated === 1 || stat.validated === "1"
                      ? "Success"
                      : "Warning"
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const MiniStatCard = ({ icon, label, value, trend, color, onClick }) => (
  <div
    onClick={onClick}
    className=" cursor-pointer bg-white p-4 md:p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-all group overflow-hidden"
  >
    <div
      className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white mb-4 shadow-md`}
    >
      {icon}
    </div>
    <div className="flex flex-col">
      <div className="text-2xl font-black text-slate-900 tracking-tighter leading-none">
        {value}
      </div>
      <div className="text-[10px] font-black text-slate-400 uppercase mt-1">
        {label}
      </div>
      <div className="text-[9px] font-black text-emerald-500 mt-2 bg-emerald-50 w-fit px-1.5 py-0.5 rounded-md">
        {trend}
      </div>
    </div>
  </div>
);

const BreakdownItem = ({ value, label, icon, color, bg, onClick }) => (
  <div
    onClick={onClick}
    className="flex flex-col items-center text-center p-3 md:p-5 rounded-2xl hover:bg-slate-50 transition-all"
  >
    <div
      className={`w-12 h-12 ${bg} ${color} rounded-xl flex items-center justify-center mb-3`}
    >
      {icon}
    </div>
    <div className="text-xl md:text-2xl font-black text-slate-900 leading-none">
      {value}
    </div>
    <div className="text-[10px] font-bold text-slate-400 mt-1 uppercase">
      {label}
    </div>
  </div>
);

const ActivityRow = ({
  site,
  tag,
  weight,
  method,
  validated,
  time,
  date,
  time2,
  formatJerusalemDateTime,
  status,
}) => (
  <tr className="hover:bg-slate-50/80 transition-all">
    <td className="px-4 py-4 font-bold text-slate-800 text-xs whitespace-nowrap">
      {site}
    </td>

    <td className="px-4 py-4">
      <span className="font-mono text-xs bg-green-50 px-2 py-1 rounded-xl border border-slate-200/50">
        {tag ? tag : method}
      </span>
    </td>
    <td className="px-4 py-4 text-center font-black text-[14px] text-slate-900">
      {weight} <span className="text-[10px] text-slate-400">kg</span>
    </td>
    <td className="px-4 py-4 text-center">
      {validated ? (
        <CheckCircle size={16} className="text-emerald-500 mx-auto" />
      ) : (
        <AlertTriangle size={16} className="text-amber-500 mx-auto" />
      )}
    </td>
    <td className="px-2 py-3 text-[11px] font-bold text-indigo-600">
      {formatJerusalemDateTime(date, time2)}
    </td>
  </tr>
);

export default Dashboard;
